/**
 * Local `document_page_mapping` for the Final Documents screen.
 *
 * Seeds from the order payload once, then appends an "Others / User Updated"
 * range after the user merges newly added PDFs. Server persistence happens
 * later on Submit.
 *
 * @module finalDocuments/hooks/useLocalDocumentPageMapping
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { DocumentPageMapping } from "../../../data-model/medicalPaOrdersModel";
import { appendUserUpdatedMapping } from "../utils/appendUserUpdatedMapping";
import { getPdfPageCount } from "../utils/getPdfPageCount";

/** Inputs for {@link useLocalDocumentPageMapping}. */
interface UseLocalDocumentPageMappingProps {
  /** Mapping from `order.clinical_document.document_page_mapping`. */
  orderMapping?: DocumentPageMapping[];
  /** True once general order details have finished loading with an order. */
  isOrderLoaded: boolean;
  /** Merge helper that returns the merged PDF file on success. */
  mergePdfsFromBase64: (
    base64Urls: string[],
    fileName?: string,
  ) => Promise<File | undefined>;
  /** PDF currently on screen (`mergedFile ?? documents[0]`), or null. */
  previousFile: File | null;
}

/**
 * Holds a session-local page mapping that can diverge from the order after
 * the user appends attachments.
 *
 * @input props - Order mapping seed, loaded flag, merge helper, and current PDF
 * @return Local mapping, dirty flag, and a merge handler that syncs mapping
 */
export const useLocalDocumentPageMapping = ({
  orderMapping,
  isOrderLoaded,
  mergePdfsFromBase64,
  previousFile,
}: UseLocalDocumentPageMappingProps) => {
  const [mapping, setMapping] = useState<DocumentPageMapping[]>([]);
  const [mappingDirty, setMappingDirty] = useState(false);
  const mappingRef = useRef<DocumentPageMapping[]>([]);
  const previousFileRef = useRef<File | null>(previousFile);
  const hasSeededRef = useRef(false);

  useEffect(() => {
    mappingRef.current = mapping;
  }, [mapping]);

  useEffect(() => {
    previousFileRef.current = previousFile;
  }, [previousFile]);

  useEffect(() => {
    if (!isOrderLoaded || hasSeededRef.current) {
      return;
    }
    const seeded = orderMapping ?? [];
    mappingRef.current = seeded;
    setMapping(seeded);
    hasSeededRef.current = true;
  }, [isOrderLoaded, orderMapping]);

  /**
   * Appends or extends the user-updated mapping row using page counts of the
   * PDF that was on screen versus the newly merged packet.
   *
   * @input priorFile - PDF shown before the merge, or null when none exists
   * @input mergedFile - Result of merging original plus newly added files
   */
  const syncMappingAfterMerge = useCallback(
    async (priorFile: File | null, mergedFile: File): Promise<void> => {
      const pagesBefore = priorFile ? await getPdfPageCount(priorFile) : 0;
      const pagesAfter = await getPdfPageCount(mergedFile);
      const next = appendUserUpdatedMapping(
        mappingRef.current,
        pagesBefore,
        pagesAfter,
      );
      if (next === mappingRef.current) {
        return;
      }
      mappingRef.current = next;
      setMapping(next);
      setMappingDirty(true);
    },
    [],
  );

  /**
   * Merges attachments, then updates local mapping from the page-count delta.
   *
   * @input base64Urls - Attachment PDFs as base64 data URLs
   * @input fileName - Optional merged output file name
   */
  const handleMergePdfs = useCallback(
    async (base64Urls: string[], fileName?: string): Promise<void> => {
      const priorFile = previousFileRef.current;
      const nextFile = await mergePdfsFromBase64(base64Urls, fileName);
      if (nextFile) {
        await syncMappingAfterMerge(priorFile, nextFile);
      }
    },
    [mergePdfsFromBase64, syncMappingAfterMerge],
  );

  return {
    documentPageMapping: mapping,
    mappingDirty,
    handleMergePdfs,
  };
};
